import { defineStore } from 'pinia';
import { getControllersCount } from '@/api/controllers';
import {
  sendInviteToOrganization,
  validateInvite,
  acceptInvite,
  enrollToOrganization,
  getOrganizations,
  getOrganization,
  leaveOrganization,
  deleteOrganization,
  getMembers,
  removeMember,
  changeRole,
  checkGrafanaAccess,
  addOrganization,
  updateOrganization,
  transferControllersInternal,
  transferControllersExternal,
  validateControllersTransfer,
  acceptControllersTransfer,
  cancelControllersTransfer,
  getExternalTransfer,
} from '@/api/organizations';
import type {
  FetchedItems,
  FetchedItem,
  Organization,
  OrganizationMember,
  ControllersTransfer,
  Role,
} from '@/types';

export const useOrganizationsStore = defineStore('organizationsStore', {
  state: (): {
    organizations: FetchedItems<Organization>;
    organization: FetchedItem<Organization>;
    members: FetchedItems<OrganizationMember>;
    controllersCount: number;
  } => ({
    organizations: undefined,
    organization: undefined,
    members: undefined,
    controllersCount: 0,
  }),
  actions: {
    async sendInviteToOrganization(
      organizationId: string,
      email: string,
    ): Promise<{ id: string }> {
      try {
        return await sendInviteToOrganization(organizationId, email);
      } catch (error: any) {
        return Promise.reject(error);
      }
    },
    async validateInvite(token: string) {
      try {
        return await validateInvite(token);
      } catch (error: any) {
        return Promise.reject(error);
      }
    },
    async acceptInvite(token: string, password: string) {
      try {
        return await acceptInvite(token, password);
      } catch (error: any) {
        return Promise.reject(error);
      }
    },
    async enrollToOrganization(token: string) {
      try {
        return await enrollToOrganization(token);
      } catch (error: any) {
        return Promise.reject(error);
      }
    },
    async getOrganizations() {
      try {
        this.organizations = await getOrganizations();
      } catch (error: any) {
        return Promise.reject(error);
      }
    },
    async addOrganization(name: string) {
      try {
        return await addOrganization(name);
      } catch (error: any) {
        return Promise.reject(error);
      }
    },
    async getOrganization(organizationId: string) {
      try {
        this.organization = await getOrganization(organizationId);
      } catch (error: any) {
        return Promise.reject(error);
      }
    },
    async updateOrganization({
      organizationId,
      name,
      totpRequired,
    }: {
      organizationId: string;
      name?: string;
      totpRequired?: boolean;
    }) {
      try {
        await updateOrganization({ organizationId, name, totpRequired });
        await this.getOrganization(organizationId);
      } catch (error: any) {
        return Promise.reject(error);
      }
    },
    async leaveOrganization(organizationId: string) {
      try {
        await leaveOrganization(organizationId);
        await this.getOrganizations();
      } catch (error: any) {
        return Promise.reject(error);
      }
    },
    async deleteOrganization(organizationId: string): Promise<void> {
      try {
        await deleteOrganization(organizationId);
      } catch (error: any) {
        return Promise.reject(error);
      }
    },
    async getMembers(organizationId: string) {
      try {
        this.members = await getMembers(organizationId);
      } catch (error: any) {
        return Promise.reject(error);
      }
    },
    async getControllersCount(organizationId: string) {
      try {
        const { count } = await getControllersCount({ organizationId });
        this.controllersCount = count;
      } catch (error: any) {
        return Promise.reject(error);
      }
    },
    async removeMember({
      organizationId,
      userId,
    }: {
      organizationId: string;
      userId: string;
    }) {
      try {
        await removeMember({ organizationId, userId });
      } catch (error: any) {
        return Promise.reject(error);
      }
    },
    async changeRole({
      organizationId,
      userId,
      role,
    }: {
      organizationId: string;
      userId: string;
      role: Role;
    }) {
      try {
        await changeRole({ organizationId, userId, role });
      } catch (error: any) {
        return Promise.reject(error);
      }
    },
    async checkGrafanaAccess(organizationId: string) {
      try {
        return await checkGrafanaAccess(organizationId).then((res) => {
          return res || false;
        });
      } catch {
        return false;
      }
    },
    async transferControllersInternal(
      targetOrganizationId: string,
      controllers: string[],
      clearControllersInfo: boolean,
    ) {
      try {
        return await transferControllersInternal(
          this.organization!.id,
          targetOrganizationId,
          controllers,
          clearControllersInfo,
        );
      } catch (error: any) {
        return Promise.reject(error);
      }
    },
    async transferControllersExternal(targetEmail: string, controllers: string[], clearControllersInfo: boolean) {
      try {
        return await transferControllersExternal(this.organization!.id, targetEmail, controllers, clearControllersInfo);
      } catch (error: any) {
        return Promise.reject(error);
      }
    },
    async validateControllersTransfer(token: string) {
      try {
        return await validateControllersTransfer(token);
      } catch (error: any) {
        return Promise.reject(error);
      }
    },
    async acceptControllersTransfer(token: string, targetOrganizationId: string) {
      try {
        return await acceptControllersTransfer(token, targetOrganizationId);
      } catch (error: any) {
        return Promise.reject(error);
      }
    },
    async getExternalTransfer(transferRequestId: string): Promise<ControllersTransfer> {
      try {
        return await getExternalTransfer(this.organization!.id, transferRequestId);
      } catch (error: any) {
        return Promise.reject(error);
      }
    },
    async cancelControllersTransfer(transferRequestId: string) {
      try {
        return await cancelControllersTransfer(this.organization!.id, transferRequestId);
      } catch (error: any) {
        return Promise.reject(error);
      }
    },
  },
});
